import React, { useState } from 'react';
import { Users, Crown, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from './Dialog';
import { Button } from './Button';
import { Textarea } from './Textarea';
import { Label } from './Label';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { useConductor } from '../helpers/useConductor';
import { toast } from 'sonner';
import styles from './ConductorDialog.module.css';

interface ConductorDialogProps {
  sessionId: string;
  disabled?: boolean;
}

export const ConductorDialog: React.FC<ConductorDialogProps> = ({ sessionId, disabled }) => {
  const [open, setOpen] = useState(false);
  const [goal, setGoal] = useState('');
  const { mutate: runConductor, isPending } = useConductor();

  const handleSubmit = () => {
    if (!goal.trim()) {
      toast.error('Please enter a goal for the agent team');
      return;
    }

    runConductor(
      {
        sessionId,
        goal: goal.trim(),
        maxIterations: 25,
        autoExecute: true,
      },
      {
        onSuccess: (data) => {
          toast.success(
            `Agent team completed: ${data.tasks.filter((t) => t.status === 'completed').length}/${data.tasks.length} tasks`,
          );
          setOpen(false);
          setGoal('');
        },
        onError: (error) => {
          toast.error(`Agent team failed: ${error.message}`);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={disabled}
              className={styles.triggerButton}
            >
              <Crown size={14} />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>Agent Team</TooltipContent>
      </Tooltip>
      <DialogContent
        className={styles.dialogContent}
        // Prevent dialog from closing on touch events that might propagate from the trigger
        onPointerDownOutside={(e) => {
          // On mobile, prevent accidental close from touch events
          if (e.detail.originalEvent.type === 'pointerdown') {
            const target = e.target as HTMLElement;
            // Only close if clicking on the actual overlay, not on touch propagation
            if (!target.closest('[data-radix-dialog-content]')) {
              // Allow normal close behavior
            }
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className={styles.title}>
            <Crown size={20} className={styles.titleIcon} />
            Multi-Agent Vibe Coder
          </DialogTitle>
          <DialogDescription>
            Describe your goal and let the AI agent team work together to accomplish it.
          </DialogDescription>
        </DialogHeader>

        <div className={styles.agentList}>
          <div className={styles.agentItem}>
            <span className={styles.agentIcon}>👑</span>
            <div>
              <strong>Conductor</strong>
              <span className={styles.agentDesc}>Orchestrates the team</span>
            </div>
          </div>
          <div className={styles.agentItem}>
            <span className={styles.agentIcon}>📋</span>
            <div>
              <strong>Planner</strong>
              <span className={styles.agentDesc}>Breaks goal into tasks</span>
            </div>
          </div>
          <div className={styles.agentItem}>
            <span className={styles.agentIcon}>🔨</span>
            <div>
              <strong>Builder</strong>
              <span className={styles.agentDesc}>Writes the code</span>
            </div>
          </div>
          <div className={styles.agentItem}>
            <span className={styles.agentIcon}>🎨</span>
            <div>
              <strong>Designer</strong>
              <span className={styles.agentDesc}>Polishes UI/UX</span>
            </div>
          </div>
          <div className={styles.agentItem}>
            <span className={styles.agentIcon}>🔍</span>
            <div>
              <strong>Reviewer</strong>
              <span className={styles.agentDesc}>Reviews for quality</span>
            </div>
          </div>
          <div className={styles.agentItem}>
            <span className={styles.agentIcon}>🧪</span>
            <div>
              <strong>QA</strong>
              <span className={styles.agentDesc}>Tests the result</span>
            </div>
          </div>
        </div>

        <div className={styles.form}>
          <div className={styles.field}>
            <Label htmlFor="goal">What do you want to build?</Label>
            <Textarea
              id="goal"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="e.g., Add a dark mode toggle to the settings page, Create a user profile component with avatar upload..."
              rows={4}
              disabled={isPending}
            />
          </div>

        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending || !goal.trim()}>
            {isPending ? (
              <>
                <Loader2 size={14} className={styles.spinner} />
                Running...
              </>
            ) : (
              <>
                <Users size={14} />
                Start Agent Team
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
